import { useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'
import { RootState } from 'App'
import { USER_ROLES } from 'config'
import { useFeatureFlag } from 'hooks/userFeatureFlag'

// The floating chat is mounted next to the portal's <Route> (see
// routes/PrivateRoute.tsx), not inside it, so useParams() can't see
// :clientId. Matching the URL directly works from anywhere under the Router.
const CLIENT_ROUTE_PATHS = [
  '/advisor/:tenantId/clients/:clientId',
  '/admin/:tenantId/clients/:clientId',
]

/**
 * When an advisor or EDO admin is on a client's page, the coach talks about
 * that client's business. Returns the client's business id, or nothing.
 * Only with the new coach (release-coach-agent-v2), which asks the advisor to
 * confirm every change; the old one would change the client's data without asking.
 * `ready` is false while that flag has not answered yet on a client page: the
 * target can still change from nothing to the client.
 */
export const useCoachTarget = (): { clientId?: string; ready: boolean } => {
  const user = useSelector((state: RootState) => state.user.user)
  const roles: string[] = user?.roles ?? []
  const match: { params: { clientId?: string } } | null = useRouteMatch({
    path: CLIENT_ROUTE_PATHS,
  })
  const clientCoachEnabled = useFeatureFlag(user?.id || '', 'release-coach-agent-v2')

  const isAdvisorOrAdmin =
    roles.includes(USER_ROLES.BSO_ADVISOR) || roles.includes(USER_ROLES.BSO_ADMIN)
  if (!isAdvisorOrAdmin || !match?.params?.clientId) return { ready: true }
  if (!clientCoachEnabled) return { ready: clientCoachEnabled !== undefined }

  return { clientId: match.params.clientId, ready: true }
}
